// `utils` is an untyped .js module; imports resolve as `any`.
import { isStringMatchRelaxed, convertToStringMatchRelaxed } from 'utils';

interface LicenseCostCenter {
  name: string;
  owner: string;
}

interface LicenseProductAccess {
  name: string;
  type: string;
}

export interface LicenseData {
  costCenter: LicenseCostCenter;
  productAccess: LicenseProductAccess;
}

interface GetExistingMatchArgs {
  costCenterName: string;
  licensesDataArray: LicenseData[];
  ownerId: string;
}

export const getExistingMatch = ({
  costCenterName,
  licensesDataArray,
  ownerId,
}: GetExistingMatchArgs): LicenseData | undefined => {
  let result: LicenseData | undefined;
  // check to make sure array is not empty
  if (!Array.isArray(licensesDataArray) || !licensesDataArray.length) return undefined;
  // iterate until a matching cost center name and owner id
  // since cost center names are unique, should be only one result
  // disabled lint rule as it is more efficient to break out of loop immedately upon a match;

  for (const element of licensesDataArray) {
    if (
      isStringMatchRelaxed(element.costCenter.name, costCenterName) &&
      element.costCenter.owner === ownerId
    ) {
      result = element;
      break;
    }
  }
  return result;
};

interface HasMatchingProductArgs {
  costCenterName: string;
  productName: string;
  productAccessType: string;
  licensesDataArray: LicenseData[];
}

export const hasMatchingProductNameAndProductAccessType = ({
  costCenterName,
  productName,
  productAccessType,
  licensesDataArray,
}: HasMatchingProductArgs): boolean => {
  let result = false;
  // if the supplied costCenterName or data array are undefined, return immedately
  if (!costCenterName || !licensesDataArray || !licensesDataArray.length) return result;
  // disabled lint rule as it is more efficient to break out of loop immedately upon a match;

  for (const element of licensesDataArray) {
    const isCostCenterNameMatch = isStringMatchRelaxed(element.costCenter.name, costCenterName);
    const { name, type } = element.productAccess;
    if (isCostCenterNameMatch && name === productName && productAccessType === type) {
      result = true;
      break;
    }
  }
  // otherwise, return false
  return result;
};

export const validateDuplicateCostCenterName = (
  preEditCostCenterName: string | undefined,
  costCenterName: string,
  nameListRelaxed: string[],
): boolean => {
  // if there is an existing cost center name (editing the license) but the user has changed it to an empty string, then return as invalid
  if (preEditCostCenterName && !costCenterName) return false;
  const comparisonString = convertToStringMatchRelaxed(costCenterName);
  const preEditComparisonString = convertToStringMatchRelaxed(preEditCostCenterName);
  // pre-edit value is populated ( existing cost center name is being edited) and is the same as the proposed cost center name, then the name hasn't changed
  if (preEditCostCenterName && preEditComparisonString === comparisonString) return true;

  // convertToStringMatchRelaxed returns undefined for a falsy costCenterName; the
  // cast preserves the original runtime call (includes(undefined) -> false here).
  const isDuplicateName = nameListRelaxed.includes(comparisonString as string);
  // if pre-edit is undefined, then then a new cost center is being created and there is a duplicate on the list, validation should fail;
  // if the pre-edit name is populated (existing license is being edited) and the proposed cost center name is already on the list, validation should also fail
  if (isDuplicateName) return false;
  // otherwise, the costCenterName value is not a duplicate
  return true;
};

export const costCenterDuplicateValidationMessage = (): string => {
  return `Must be unique for indexing purposes. This cost center name already exists.`;
};
